import React, { useState, useEffect } from "react";
import TrackItem from "./TrackItem";
import TransitionBlock from "./TransitionBlock";
import AnimationBlock from "./AnimationBlock";

export default function TrackView({
  images,
  setImages,                     // REQUIRED
  durations,
  setDurations,                  // REQUIRED
  onDurationChange,
  pxPerSec,
  zoom,
  clipTransitions,
  setClipTransitions,
  onSelectTransition,
  onSelectAnimation,
  clipAnimations,
  setClipAnimations,
  clipAnimationTiming,
  setClipAnimationTiming,
  animationDuration,
}) {
  const scale = pxPerSec * zoom;

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  /* ───────── Reorder helper ───────── */
  const reorder = (arr, from, to) => {
    if (!arr || from === to) return arr;
    const next = [...arr];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  };

  /* ───────── Commit reorder ───────── */
  const commitReorder = () => {
    if (
      dragIndex === null ||
      overIndex === null ||
      dragIndex === overIndex
    ) {
      return;
    }

    setImages((prev) => reorder(prev, dragIndex, overIndex));
    setDurations((prev) => reorder(prev, dragIndex, overIndex));
    setClipTransitions((prev) => reorder(prev, dragIndex, overIndex));
    setClipAnimations((prev) => reorder(prev, dragIndex, overIndex));
    setClipAnimationTiming((prev) => reorder(prev, dragIndex, overIndex));
  };

  /* ───────── Global mouse-up (CRITICAL FIX) ───────── */
  useEffect(() => {
    if (dragIndex === null) return;

    const handleMouseUp = () => {
      commitReorder();
      setDragIndex(null);
      setOverIndex(null);
    };

    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [dragIndex, overIndex]);

  return (
    <div
      className={`
        w-full h-30 overflow-x-auto
        flex items-start px-2 gap-4
        rounded-md
        bg-gray-200 dark:bg-gray-900
        border border-gray-200 dark:border-gray-800
        transition-colors
        ${dragIndex !== null ? "select-none cursor-grabbing" : ""}
      `}
    >
      {images.map((img, idx) => {
        if (durations[idx] === undefined) return null;

        const isDragging = idx === dragIndex;
        const isTarget = idx === overIndex && dragIndex !== null;

        return (
          <div
            key={idx}
            className={`
              flex flex-col gap-2 transition-transform
              ${isDragging ? "opacity-50" : ""}
              ${isTarget ? "scale-105" : ""}
              ${dragIndex !== null && idx !== dragIndex ? "opacity-70" : ""}

            `}
            onMouseEnter={() =>
              dragIndex !== null && setOverIndex(idx)
            }
          >
            {/* ───────── TOP ROW ───────── */}
            <div className="flex items-center gap-2">
              <TrackItem
                index={idx}
                img={img}
                duration={durations[idx]}
                width={durations[idx] * scale}
                onDurationChange={onDurationChange}
                onDragStart={() => setDragIndex(idx)}
                onSelectAnimation={() => onSelectAnimation(idx)}
              />

              {idx < images.length - 1 && (
                <TransitionBlock
                  index={idx}
                  currentTransition={clipTransitions[idx]}
                  onSelectTransition={onSelectTransition}
                  previewImages={[images[idx], images[idx + 1]]}
                />
              )}
            </div>

            {/* ───────── BOTTOM ROW ───────── */}
            <div
              className="
                w-full rounded-sm px-1
                bg-gray-100 dark:bg-gray-800
                border border-gray-200 dark:border-gray-700
                transition-colors
              "
            >
              <AnimationBlock
                index={idx}
                clipAnimationTiming={clipAnimationTiming?.[idx]}
                setClipAnimationTiming={setClipAnimationTiming}
                currentAnimation={clipAnimations?.[idx]}
                animationDuration={animationDuration}
                onSelectAnimation={onSelectAnimation}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
